import { cn, Text, IconButton, type IconButtonProps } from '@bridebook/aura';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';

type AppBarProps = Omit<HTMLAttributes<HTMLDivElement>, 'title'> & {
  /**
   * Left element - typically a back navigation button
   */
  leftElement?: ReactNode;
  /**
   * Center title text or element
   */
  header: ReactNode | string;
  /**
   * Right element - typically close (navigation) or settings (action) buttons
   */
  rightElement?: ReactNode;
  /**
   * Optional tabs section below the main bar
   */
  children?: ReactNode;
  classNames?: {
    topWrapper?: string;
    title?: string;
    leftAction?: string;
    rightAction?: string;
    childrenWrapper?: string;
  };
};

export const AppBar = forwardRef<HTMLDivElement, AppBarProps>(
  ({ className, classNames, leftElement, header, rightElement, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        // using inset shadow to avoid extra 1px of height that would be added by a border
        'flex w-full flex-col items-center bg-white shadow-[inset_0_-1px_0_0_var(--color-space-15)]',
        className,
      )}
      {...props}>
      <div
        className={cn(
          'grid h-app-bar-mobile-height w-full grid-cols-[1fr_auto_1fr] items-center gap-3 px-4',
          classNames?.topWrapper,
        )}>
        {/* LEFT ELEMENT */}
        <div
          className={cn(
            'flex min-w-[32px] shrink-0 items-center justify-start',
            classNames?.leftAction,
          )}>
          {leftElement}
        </div>
        {/* TITLE */}
        {typeof header === 'string' ? (
          <Text variant="typo-base-semibold" className="min-w-0 truncate text-center text-space">
            {header}
          </Text>
        ) : (
          header
        )}
        {/* RIGHT ELEMENT */}
        <div
          className={cn(
            'flex min-w-[32px] shrink-0 items-center justify-end',
            classNames?.rightAction,
          )}>
          {rightElement}
        </div>
      </div>
      {children && (
        <div className={cn('w-full overflow-x-auto', classNames?.childrenWrapper)}>{children}</div>
      )}
    </div>
  ),
);
AppBar.displayName = 'AppBar';

/**
 * Navigation button for AppBar - typically used for back/close actions on the left side
 */
export const AppBarButtonNavigation = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = 'grey', size = 'sm', ...props }, ref) => (
    <IconButton ref={ref} variant={variant} size={size} {...props} />
  ),
);
AppBarButtonNavigation.displayName = 'AppBarButtonNavigation';

/**
 * Action button for AppBar - typically used for settings/info/actions on the right side
 */
export const AppBarButtonAction = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ variant = 'white', size = 'sm', ...props }, ref) => (
    <IconButton ref={ref} variant={variant} size={size} {...props} />
  ),
);
AppBarButtonAction.displayName = 'AppBarButtonAction';
